import { autocompletion, completeFromList } from '@codemirror/autocomplete';
import { StreamLanguage } from '@codemirror/language';
import { type Extension } from '@uiw/react-codemirror';
import constate from 'constate';
import { useCallback, useMemo } from 'react';

import { api } from 'common';

import { DOUBLE_BRACE_AFFIXES, getAutocompleteCompletions } from './autocompleteUtils';
import { NODE_FIELD_NAMES_BY_NODE_TYPE } from './nodeFieldNames';
import { nodeNameHighlightTheme } from './theme';
import {
  getNodeNameInBracesTokenizer,
  getNodeNameTokenizer,
  isParsedFieldValid,
} from './tokenUtils';
import { type AutocompleteLabels, CodeMirrorLanguageType } from './types';
import { useStableValue } from '../../../../hooks/useStableValue';
import { useReactFlowViewContext } from '../ReactFlow/context/context';
import { getNodes } from '../ReactFlow/context/useFlowViewRelationships';
import { useFlowDefinitionContext } from '../context';
import { useFindLatestTestRunApi } from '../useApiFindLatestTestRun';
import { useApiFindSdkCompletions } from '../useApiFindSdkCompletions';

interface UseLanguagesConfig {
  nodeId: string;
  additionalAutoCompleteLabels?: AutocompleteLabels;
  additionalAutoCompleteNodeIds?: string[];
}

interface UseCodeMirrorConfig extends UseLanguagesConfig {}

export interface UseAutocompleteConfig {
  nodeId?: string;
  /**
   * @deprecated Use BlockValueTemplateFieldWrapper instead
   */
  memoizedSelectOptions?: { label: string; value: string }[];
  memoizedAdditionalAutoCompleteLabels?: AutocompleteLabels;
  memoizedAdditionalAutoCompleteNodeIds?: string[];
}

export interface CodeMirrorAutocompleteContextValue {
  flowTemplateNodeNameExtensions: Extension[];
  expressionNodeNameExtensions: Extension[];
  javascriptNodeNameExtensions: Extension[];
}

function useCodeMirrorLanguages({
  nodeId,
  additionalAutoCompleteLabels,
  additionalAutoCompleteNodeIds,
}: UseLanguagesConfig) {
  const { nodesById, namesById, getAllParentNodeIds, startBlockNode } = useReactFlowViewContext();

  const flowParameterNames = useMemo(
    () => startBlockNode?.data.config.parameters?.map(({ name }) => name) ?? [],
    [startBlockNode]
  );
  const allowedNodeIds = useMemo(
    () => [...new Set([...getAllParentNodeIds(nodeId), ...(additionalAutoCompleteNodeIds ?? [])])],
    [nodeId, getAllParentNodeIds, additionalAutoCompleteNodeIds]
  );
  const allowedVariableNames = useMemo(
    () => allowedNodeIds.map((id) => namesById[id]),
    [namesById, allowedNodeIds]
  );

  const flowTemplateNodeNameLanguage = useMemo(
    () =>
      StreamLanguage.define(
        getNodeNameInBracesTokenizer({
          allowedVariableNames,
          flowParameterNames,
          additionalAutoCompleteLabels,
        })
      ),
    [additionalAutoCompleteLabels, flowParameterNames, allowedVariableNames]
  );

  const expressionNodeNameLanguage = useMemo(
    () =>
      StreamLanguage.define(
        getNodeNameTokenizer({
          allowedVariableNames,
          flowParameterNames,
          additionalAutoCompleteLabels,
        })
      ),
    [additionalAutoCompleteLabels, flowParameterNames, allowedVariableNames]
  );

  const javascriptNodeNameLanguage = useMemo(
    () =>
      StreamLanguage.define(
        getNodeNameTokenizer({
          allowedVariableNames,
          flowParameterNames,
          isJavascriptAllowed: true,
          additionalAutoCompleteLabels,
        })
      ),
    [additionalAutoCompleteLabels, flowParameterNames, allowedVariableNames]
  );

  const languagesByType: Record<CodeMirrorLanguageType, StreamLanguage<unknown>> = useMemo(() => {
    return {
      [CodeMirrorLanguageType.DoubleBraceNodeFields]: flowTemplateNodeNameLanguage,
      [CodeMirrorLanguageType.NodeFields]: expressionNodeNameLanguage,
      [CodeMirrorLanguageType.JavaScriptAndNodeFields]: javascriptNodeNameLanguage,
    };
  }, [flowTemplateNodeNameLanguage, expressionNodeNameLanguage, javascriptNodeNameLanguage]);

  return {
    nodesById,
    allowedNodeIds,
    allowedVariableNames,
    flowParameterNames,
    languagesByType,
    flowTemplateNodeNameLanguage,
    expressionNodeNameLanguage,
    javascriptNodeNameLanguage,
  };
}

export function useCodeMirrorConfig(props: UseCodeMirrorConfig) {
  const { nodeId, additionalAutoCompleteLabels } = props;

  const { languagesByType } = useCodeMirrorLanguages({
    nodeId,
    additionalAutoCompleteLabels,
  });

  const validateFieldValueLanguages = useCallback(
    (fieldValuesByLanguage: Partial<Record<CodeMirrorLanguageType, (string | undefined)[]>>) => {
      for (const [languageType, fieldValues] of Object.entries(fieldValuesByLanguage)) {
        const language = languagesByType[languageType as CodeMirrorLanguageType];
        for (const fieldValue of fieldValues) {
          if (!isParsedFieldValid(language, fieldValue)) {
            return false;
          }
        }
      }
      return true;
    },
    [languagesByType]
  );

  return {
    validateFieldValueLanguages,
  };
}

function useCodeMirrorAutocomplete(
  args: UseAutocompleteConfig
): CodeMirrorAutocompleteContextValue {
  const {
    nodeId: _nodeId,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    memoizedSelectOptions,
    memoizedAdditionalAutoCompleteLabels: additionalAutoCompleteLabels,
    memoizedAdditionalAutoCompleteNodeIds: additionalAutoCompleteNodeIds,
  } = args;
  const { flowDefinition } = useFlowDefinitionContext();
  const { selectedNodeId = 'INVALID_BLOCK_ID' } = useReactFlowViewContext();
  const { data: latestTestRun } = useFindLatestTestRunApi({
    flowDefinitionId: flowDefinition?.id,
    staleTime: 5 * 60 * 1000,
  });
  const { data: sdkCompletions } = useApiFindSdkCompletions();
  const nodeId = _nodeId ?? selectedNodeId;
  const {
    nodesById,
    allowedNodeIds,
    flowParameterNames,
    flowTemplateNodeNameLanguage,
    expressionNodeNameLanguage,
    javascriptNodeNameLanguage,
    allowedVariableNames,
  } = useCodeMirrorLanguages({
    nodeId,
    additionalAutoCompleteLabels,
    additionalAutoCompleteNodeIds,
  });

  const nodeFieldNamesByNodeName = useStableValue(
    getNodes(nodesById, allowedNodeIds).reduce<Record<string, string[]>>((memo, node) => {
      const nodeFieldNames = NODE_FIELD_NAMES_BY_NODE_TYPE[node.data.uiBlockType];
      if (nodeFieldNames) {
        memo[node.data.label] = nodeFieldNames;
      }
      return memo;
    }, {})
  );

  const selectCompletions = useMemo(() => {
    if (!memoizedSelectOptions) {
      return [];
    }
    return memoizedSelectOptions.map((option) => ({
      label: option.value,
      displayLabel: option.label,
      type: 'variable',
      boost: Infinity,
    }));
  }, [memoizedSelectOptions]);

  const flowTemplateNodeNameExtensions = useMemo((): Extension[] => {
    return [
      flowTemplateNodeNameLanguage,
      flowTemplateNodeNameLanguage.data.of({
        autocomplete: completeFromList([
          ...selectCompletions,
          ...getAutocompleteCompletions({
            availableVariableNames: allowedVariableNames,
            flowParameterNames,
            dataFieldNamesByVariableName: nodeFieldNamesByNodeName,
            latestTestRun,
            /**
             * Affixes here works in conjunction with codemirror's `closeBrackets` option.
             * We used to use `prefix: {{` and displaySuffix }}` with `closeBrackets: true` (the default).
             * This causes user errors due to typing `{` and getting an autocomplete like `{{value.data}`. (Missing last brace)
             * The `displaySuffix` helped avoid with extra closing braces, like `{{value.data}}}}`
             *
             * The better solution is to use `prefix: {{` and `suffix: }}` with `closeBrackets: false`.
             * This works for most cases, and is less error-prone. On occasion, someone may edit an existing value with `{{` and `}}` and end up with an extra `}}`.
             * This is less common.
             *
             * Note - If we don't have a prefix, then writing a prompt will autocomplete during text editing as well.
             */
            affixes: DOUBLE_BRACE_AFFIXES,
            additionalAutoCompleteLabels,
          }),
        ]),
      }),
      nodeNameHighlightTheme,
    ];
  }, [
    latestTestRun,
    selectCompletions,
    additionalAutoCompleteLabels,
    flowTemplateNodeNameLanguage,
    allowedVariableNames,
    flowParameterNames,
    nodeFieldNamesByNodeName,
  ]);

  const expressionNodeNameExtensions = useMemo((): Extension[] => {
    return [
      expressionNodeNameLanguage,
      expressionNodeNameLanguage.data.of({
        autocomplete: completeFromList([
          ...selectCompletions,
          ...getAutocompleteCompletions({
            availableVariableNames: allowedVariableNames,
            flowParameterNames,
            dataFieldNamesByVariableName: nodeFieldNamesByNodeName,
            latestTestRun,
            additionalAutoCompleteLabels,
          }),
        ]),
      }),
      nodeNameHighlightTheme,
    ];
  }, [
    latestTestRun,
    selectCompletions,
    additionalAutoCompleteLabels,
    expressionNodeNameLanguage,
    allowedVariableNames,
    flowParameterNames,
    nodeFieldNamesByNodeName,
  ]);

  const javascriptNodeNameExtensions = useMemo((): Extension[] => {
    return [
      autocompletion(),
      javascriptNodeNameLanguage,
      javascriptNodeNameLanguage.data.of({
        autocomplete: completeFromList([
          ...selectCompletions,
          ...getAutocompleteCompletions({
            availableVariableNames: allowedVariableNames,
            dataFieldNamesByVariableName: nodeFieldNamesByNodeName,
            flowParameterNames,
            latestTestRun,
            sdkCompletions,
            additionalAutoCompleteLabels,
          }).concat(
            api.FlowDefinition.javascriptKeywords.map((keyword) => ({
              label: keyword,
              type: 'keyword',
            }))
          ),
        ]),
      }),
      nodeNameHighlightTheme,
    ];
  }, [
    latestTestRun,
    selectCompletions,
    sdkCompletions,
    additionalAutoCompleteLabels,
    javascriptNodeNameLanguage,
    allowedVariableNames,
    flowParameterNames,
    nodeFieldNamesByNodeName,
  ]);

  return {
    flowTemplateNodeNameExtensions,
    expressionNodeNameExtensions,
    javascriptNodeNameExtensions,
  };
}

export const [CodeMirrorAutocompleteProvider, useCodeMirrorAutocompleteContext] =
  constate(useCodeMirrorAutocomplete);
